import { useReadOnly } from 'utils/lifecycle'
import React, { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material'
import { useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import AddressSearch from 'components/address/AddressSearch'
import api from 'utils/api'

const alertTypes = {
  KEYWORD: 'KEYWORD',
  BUSINESS: 'BUSINESS',
  CATEGORY: 'CATEGORY',
}

const locationLabel = (location) => [
  location?.city,
  location?.region_name || location?.region_code,
  location?.country_code || location?.country_name,
].filter(Boolean).join(', ')

const isUsableLocation = (location) => {
  const latitude = Number(location?.latitude)
  const longitude = Number(location?.longitude)
  return Number.isFinite(latitude)
    && Number.isFinite(longitude)
    && latitude >= -90
    && latitude <= 90
    && longitude >= -180
    && longitude <= 180
    && !(latitude === 0 && longitude === 0)
}

const normalizeLocation = (location, source) => {
  if (!isUsableLocation(location)) return null
  return {
    ...location,
    latitude: Number(location.latitude),
    longitude: Number(location.longitude),
    label: location.fullAddressResult || locationLabel(location) || 'Selected location',
    source,
  }
}

const CreateAlertDialog = ({ open, onClose, onCreated, initialValue }) => {
  const readOnly = useReadOnly()
  const { enqueueSnackbar } = useSnackbar()
  const headerLocation = useSelector((state) => state.user.location)
  const profileAddress = useSelector((state) => state.user.details?.address)
  const [type, setType] = useState(alertTypes.KEYWORD)
  const [keyword, setKeyword] = useState('')
  const [business, setBusiness] = useState(null)
  const [category, setCategory] = useState(null)
  const [businessOptions, setBusinessOptions] = useState([])
  const [categoryOptions, setCategoryOptions] = useState([])
  const [submitting, setSubmitting] = useState(false)
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [resolvingProfile, setResolvingProfile] = useState(false)
  const [profileResolutionFailed, setProfileResolutionFailed] = useState(false)
  const [showLocationChoices, setShowLocationChoices] = useState(false)
  const [showManualAddress, setShowManualAddress] = useState(false)
  const [locating, setLocating] = useState(false)
  const [locationMessage, setLocationMessage] = useState('')
  const locationRequest = useRef(0)

  useEffect(() => {
    if (!open) return
    setType(initialValue?.type || alertTypes.KEYWORD)
    setKeyword(initialValue?.keyword || '')
    setBusiness(initialValue?.business || null)
    setCategory(initialValue?.category || null)
    setBusinessOptions(initialValue?.business ? [initialValue.business] : [])
    setCategoryOptions(initialValue?.category ? [initialValue.category] : [])
    if (initialValue?.type === alertTypes.CATEGORY
        && initialValue.category?.name
        && !initialValue.category.categoryId) {
      api.get(`/category?size=20&name=${encodeURIComponent(initialValue.category.name)}`)
        .then((response) => {
          const exact = (response.content || []).find((option) => (
            option.name?.toLowerCase() === initialValue.category.name.toLowerCase()
          ))
          if (exact) {
            setCategory(exact)
            setCategoryOptions(response.content || [])
          }
        })
        .catch(() => setCategoryOptions([]))
    }
  }, [initialValue, open])

  useEffect(() => {
    const requestId = ++locationRequest.current
    setLocating(false)
    if (!open) return undefined

    const fallback = normalizeLocation(headerLocation, 'header')
    setProfileResolutionFailed(false)
    setShowLocationChoices(false)
    setShowManualAddress(false)
    setLocationMessage('')

    if (!profileAddress?.trim()) {
      setSelectedLocation(fallback)
      setResolvingProfile(false)
      setShowLocationChoices(!fallback)
      return undefined
    }

    setSelectedLocation(null)
    setResolvingProfile(true)
    api.post('/geo/resolve', { address: profileAddress.trim() })
      .then((resolved) => {
        if (locationRequest.current !== requestId) return
        const profileLocation = normalizeLocation(resolved, 'profile')
        if (profileLocation) {
          setSelectedLocation(profileLocation)
          return
        }
        setProfileResolutionFailed(true)
        setSelectedLocation(fallback)
        setShowLocationChoices(!fallback)
      })
      .catch(() => {
        if (locationRequest.current !== requestId) return
        setProfileResolutionFailed(true)
        setSelectedLocation(fallback)
        setShowLocationChoices(!fallback)
      })
      .finally(() => {
        if (locationRequest.current === requestId) setResolvingProfile(false)
      })

    return undefined
  }, [headerLocation, open, profileAddress])

  const hasLocation = isUsableLocation(selectedLocation)
  const canSubmit = !readOnly && hasLocation && !resolvingProfile && !submitting && (
    (type === alertTypes.KEYWORD && keyword.trim())
    || (type === alertTypes.BUSINESS && business?.businessId)
    || (type === alertTypes.CATEGORY && category?.categoryId)
  )

  const currentLocation = selectedLocation?.label || ''

  const searchBusinesses = (_, value, reason) => {
    if (reason !== 'input' || !value.trim()) return
    api.get(`/user/query/business-options?size=20&name=${encodeURIComponent(value.trim())}`)
      .then((response) => setBusinessOptions(response.content || []))
      .catch(() => setBusinessOptions([]))
  }

  const searchCategories = (_, value, reason) => {
    if (reason !== 'input' || !value.trim()) return
    api.get(`/category?size=20&name=${encodeURIComponent(value.trim())}`)
      .then((response) => setCategoryOptions(response.content || []))
      .catch(() => setCategoryOptions([]))
  }

  const applyHeaderFallback = () => {
    const fallback = normalizeLocation(headerLocation, 'header')
    setSelectedLocation(fallback)
    setLocationMessage(fallback ? '' : 'We couldn’t access your location. Enter an address to continue.')
    setShowLocationChoices(true)
  }

  const useCurrentPosition = () => {
    const requestId = ++locationRequest.current
    setLocating(true)
    setLocationMessage('')
    if (!navigator.geolocation) {
      setLocating(false)
      applyHeaderFallback()
      return
    }

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        if (locationRequest.current !== requestId) return
        api.post('/geo/resolve', {
          latitude: coords.latitude,
          longitude: coords.longitude,
        })
          .then((resolved) => {
            if (locationRequest.current !== requestId) return
            const browserLocation = normalizeLocation(resolved, 'browser')
            if (!browserLocation) {
              applyHeaderFallback()
              return
            }
            setSelectedLocation(browserLocation)
            setShowLocationChoices(false)
            setShowManualAddress(false)
          })
          .catch(() => {
            if (locationRequest.current === requestId) {
              applyHeaderFallback()
            }
          })
          .finally(() => {
            if (locationRequest.current === requestId) setLocating(false)
          })
      },
      () => {
        if (locationRequest.current !== requestId) return
        setLocating(false)
        applyHeaderFallback()
      },
      { enableHighAccuracy: false, timeout: 15_000 }
    )
  }

  const selectManualAddress = (place) => {
    const manualLocation = normalizeLocation(place, 'manual')
    if (!manualLocation) {
      setSelectedLocation(null)
      setLocationMessage('Choose a complete address from the suggestions.')
      return
    }
    setSelectedLocation(manualLocation)
    setLocationMessage('')
    setShowLocationChoices(false)
    setShowManualAddress(false)
  }

  const handleManualInputChange = (_, reason) => {
    if (reason === 'input' || reason === 'clear') {
      setSelectedLocation(null)
      setLocationMessage('Select an address from the suggestions to use it for this alert.')
    }
  }

  const submit = async () => {
    if (!canSubmit) return
    setSubmitting(true)
    const payload = {
      type,
      keyword: type === alertTypes.KEYWORD ? keyword.trim() : undefined,
      businessId: type === alertTypes.BUSINESS ? business.businessId : undefined,
      categoryId: type === alertTypes.CATEGORY ? category.categoryId : undefined,
      latitude: Number(selectedLocation.latitude),
      longitude: Number(selectedLocation.longitude),
      radius: Number(initialValue?.radius) || 15,
      location: currentLocation,
    }
    try {
      await api.post('/user/query/add', payload)
      enqueueSnackbar('Alert created', { variant: 'success' })
      onCreated?.()
      onClose()
    } catch (error) {
      enqueueSnackbar(error?.message || error?.error || 'Cannot create alert', { variant: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Create alert</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Receive one daily email when new offers match this alert.
        </Typography>
        <FormControl>
          <RadioGroup row value={type} onChange={(event) => setType(event.target.value)}>
            <FormControlLabel value={alertTypes.KEYWORD} control={<Radio />} label="Keyword" />
            <FormControlLabel value={alertTypes.BUSINESS} control={<Radio />} label="Business" />
            <FormControlLabel value={alertTypes.CATEGORY} control={<Radio />} label="Category" />
          </RadioGroup>
        </FormControl>

        <Box sx={{ mt: 2 }}>
          {type === alertTypes.KEYWORD && (
            <TextField
              autoFocus
              fullWidth
              label="Offer keyword"
              value={keyword}
              inputProps={{ maxLength: 500 }}
              onChange={(event) => setKeyword(event.target.value)}
            />
          )}
          {type === alertTypes.BUSINESS && (
            <Autocomplete
              options={businessOptions}
              value={business}
              onChange={(_, value) => setBusiness(value)}
              onInputChange={searchBusinesses}
              getOptionLabel={(option) => option?.businessName || ''}
              isOptionEqualToValue={(option, value) => option.businessId === value.businessId}
              renderInput={(params) => <TextField {...params} label="Business name" />}
            />
          )}
          {type === alertTypes.CATEGORY && (
            <Autocomplete
              options={categoryOptions}
              value={category}
              onChange={(_, value) => setCategory(value)}
              onInputChange={searchCategories}
              getOptionLabel={(option) => option?.name || ''}
              isOptionEqualToValue={(option, value) => option.categoryId === value.categoryId}
              renderInput={(params) => <TextField {...params} label="Category" />}
            />
          )}
        </Box>

        <Box sx={{ mt: 3 }}>
          {resolvingProfile ? (
            <Alert severity="info">Finding your profile address…</Alert>
          ) : hasLocation ? (
            <Alert severity="info">
              Within {Number(initialValue?.radius) || 15} miles of {currentLocation}
            </Alert>
          ) : (
            <Alert severity="warning">
              Choose a location before creating an alert.
            </Alert>
          )}
          {profileResolutionFailed && (
            <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
              {hasLocation
                ? 'We couldn’t use the address saved in your profile. The location shown above will be used for this alert.'
                : 'We couldn’t use the address saved in your profile. Choose another location below.'}
            </Typography>
          )}
          {locationMessage && (
            <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
              {locationMessage}
            </Typography>
          )}
          {!resolvingProfile && !showLocationChoices && (
            <Button
              size="small"
              sx={{ mt: 1 }}
              onClick={() => setShowLocationChoices(true)}
            >
              Use another location
            </Button>
          )}
          {!resolvingProfile && showLocationChoices && (
            <Box sx={{ mt: 1 }}>
              <Button size="small" onClick={useCurrentPosition} disabled={locating}>
                {locating ? 'Finding your position…' : 'Use my current position'}
              </Button>
              <Button
                size="small"
                onClick={() => setShowManualAddress((visible) => !visible)}
              >
                Enter location manually
              </Button>
              {showManualAddress && (
                <Box sx={{ mt: 1 }}>
                  <AddressSearch
                    label="Your address"
                    onSelect={selectManualAddress}
                    onInputChange={handleManualInputChange}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!canSubmit}>Create alert</Button>
      </DialogActions>
    </Dialog>
  )
}

export default CreateAlertDialog
